import React, { useEffect, useState, useCallback, useRef } from "react";
import type { WordModel } from "../../type/word";
import type { UserModel } from "../../type/user";
import { getUser, isAuthenticated, fetchWithAuth } from "../../auth/auth";
import { loadWords } from "../words/dataLoaders";
import { useLanguage } from "../../context/languageContext";
import { useFlashcardActions } from "./flashcardActions";
import { useSpinner } from "../../components/spinner/spinner";
import { useToast } from "../../components/toast/toast";
import { cancelSpeech, speak } from "../../components/speakButton/speech";
import SpeakButton from "../../components/speakButton/speakButton";
import DeleteButton from "../../components/deleteButton/deleteButton";
import EditButton from "../../components/editButton/editButton";
import ConfirmDialog from "../../modal/confirmDialog/confirmDialog";
import PageHeader from "../../components/pageHeader/pageHeader";
import EmptyState from "../../components/emptyState/emptyState";
import { getApiUrl } from "../../utils/getApiUrl";
import WordModal from "../../modal/addWords/addWordsModal";
import { moveToIcon } from "../../utils/config";
import { moveWord, updateWord } from "../words/wordActions";
import "./flashcards.scss";

type LanguageMode = "eng" | "pol";
type OrderMode = "shuffle" | "sequential" | "repeat";

const Flashcards: React.FC = () => {
  const [allWords, setAllWords] = useState<WordModel[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [user, setUser] = useState<UserModel | null>(null);
  const [displayWords, setDisplayWords] = useState<WordModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [languageMode, setLanguageMode] = useState<LanguageMode>("eng");
  const [orderMode, setOrderMode] = useState<OrderMode>(() => {
    const saved = localStorage.getItem("flashcards_order_mode");
    return (saved === "shuffle" || saved === "sequential" || saved === "repeat") ? saved : "sequential";
  });
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [showSpeechInfo, setShowSpeechInfo] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [dictionaries, setDictionaries] = useState<{ iddictionary: number; name: string }[]>([]);
  const { languages, selectedLanguageId } = useLanguage();
  const selectedLanguage = languages.find(l => l.idlanguage === selectedLanguageId);
  const langCode = selectedLanguage?.code || "en";
  const langName = selectedLanguage?.name;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wakeLockRef = useRef<any>(null);
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const { showSpinner, hideSpinner } = useSpinner();
  const { showToast } = useToast();
  const params = new URLSearchParams(window.location.search);
  const iddictionary = params.get("iddictionary");

  const {
    startDrag,
    moveDrag,
    endDrag,
    handleNext,
    handlePrevious,
    handleToggleAutoPlay,
    dragStartX,
  } = useFlashcardActions({
    currentIndex,
    total: displayWords.length,
    isAutoPlaying,
    setCurrentIndex,
    setIsRevealed,
    setIsAutoPlaying,
  });

  const requestWakeLock = useCallback(async () => {
    if (!user?.parameters?.flashcards_screen_on?.value) return;

    try {
      if ("wakeLock" in navigator && !wakeLockRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        wakeLockRef.current = await (navigator as any).wakeLock.request("screen");

        wakeLockRef.current.addEventListener("release", () => {
          wakeLockRef.current = null;
        });
      }
    } catch (err) {
      console.warn("Wake lock failed:", err);
    }
  }, [user?.parameters]);

  const releaseWakeLock = useCallback(async () => {
    try {
      await wakeLockRef.current?.release();
      wakeLockRef.current = null;
    } catch { /* empty */ }
  }, []);


  useEffect(() => {
    if (isAuthenticated()) {
      const userData = getUser();
      setUser(userData);
    }
  }, []);

  // Re-acquire when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && isAutoPlaying && user?.parameters?.flashcards_screen_on?.value) {
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAutoPlaying, user?.parameters, requestWakeLock]);

  // Toggle wake lock with autoplay
  useEffect(() => {
    if (isAutoPlaying && user?.parameters?.flashcards_screen_on?.value) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [isAutoPlaying, user?.parameters, requestWakeLock, releaseWakeLock]);

  // ======================
  useEffect(() => {
    if (user !== null) {
      loadWords(
        iddictionary,
        String(user.iduser),
        (words) => {
          setAllWords(words);
          // ← load in alphabetical (sequential) order to match default mode
          setDisplayWords([...words].sort((a, b) => a.eng.localeCompare(b.eng)));
          setCurrentIndex(0);
          setIsRevealed(false);
          setIsLoading(false);
        },
        () => {},
        showSpinner,
        hideSpinner,
        (words) => words,
        { column: "eng", order: "asc" },
        { current: null },
        dictionaries.map(d => d.iddictionary)
      );
    }
  }, [user, iddictionary, dictionaries]);

  useEffect(() => {
    try {
      const cached = localStorage.getItem("app_dictionaries");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setDictionaries(parsed);
        }
      }
    } catch {
      // ignore storage errors
    }

    const onUpdated = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      if (Array.isArray(detail)) {
        setDictionaries(detail);
      }
    };

    document.addEventListener("dictionaries:updated", onUpdated as EventListener);
    return () => document.removeEventListener("dictionaries:updated", onUpdated as EventListener);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowMoveMenu(false);
      }
    };

    if (showMoveMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showMoveMenu]);

  const shuffleArray = (array: WordModel[]): WordModel[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const playCurrentCard = useCallback(async () => {
    if (!isAutoPlaying) return;

    const currentWord = displayWords[currentIndex];

    if (languageMode === "eng") {
      await speak(currentWord.eng, langCode);
    } else {
      await speak(currentWord.pl, "pl");
    }

    await new Promise(r => setTimeout(r, 500));

    setIsRevealed(true);

    await new Promise(r => setTimeout(r, 300));

    if (languageMode === "eng") {
      await speak(currentWord.pl, "pl");
    } else {
      await speak(currentWord.eng, langCode);
    }

    await new Promise(r => setTimeout(r, 1500));

    if (currentIndex < displayWords.length - 1) {
      setIsRevealed(false);
      setCurrentIndex(prev => prev + 1);
    } else if (orderMode === "repeat") {
      setIsRevealed(false);
      setCurrentIndex(0);
    } else {
      setIsAutoPlaying(false);
      if (orderMode === "shuffle") {
        setDisplayWords(shuffleArray([...allWords]));
      }
      setCurrentIndex(0);
      setIsRevealed(false);
    }
  }, [isAutoPlaying, currentIndex, displayWords, languageMode, orderMode, allWords, langCode]);

  useEffect(() => {
    if (isAutoPlaying) {
      playCurrentCard();
    }

    return () => {
      cancelSpeech();
    };
  }, [isAutoPlaying, currentIndex, playCurrentCard]);

  const handleCardClick = () => {
    if (!isRevealed && !isAutoPlaying) {
      setIsRevealed(true);
    }
  };

  const handleDeleteClick = () => setShowDeleteConfirm(true);

  const syncUpdatedWords = useCallback((updatedWords: WordModel[], activeWordId?: number) => {
    setAllWords(updatedWords);

    const nextDisplayWords =
      orderMode === "shuffle"
        ? displayWords.map((word) => updatedWords.find((item) => item.idexpression === word.idexpression) ?? word)
        : [...updatedWords].sort((a, b) => a.eng.localeCompare(b.eng));

    setDisplayWords(nextDisplayWords);

    if (activeWordId !== undefined) {
      const nextIndex = nextDisplayWords.findIndex((word) => word.idexpression === activeWordId);
      if (nextIndex >= 0) {
        setCurrentIndex(nextIndex);
      }
    }
  }, [displayWords, orderMode]);

  const openEditModal = () => {
    setShowEditModal(true);
    setShowMoveMenu(false);
  };

  const handleSaveEdit = async (eng: string, pl: string) => {
    if (!currentWord.idexpression) {
      return;
    }

    const trimmedEng = eng.trim();
    const trimmedPl = pl.trim();
    if (!trimmedEng || !trimmedPl) {
      showToast("Enter both English and Polish words.", "warning");
      return;
    }

    await updateWord(
      currentWord.idexpression,
      trimmedEng,
      trimmedPl,
      allWords,
      displayWords,
      setAllWords,
      setDisplayWords,
      (words) => words,
      showSpinner,
      hideSpinner,
      showToast,
      { column: "eng", order: "asc" }
    );

    const updatedWords = allWords.map((word) => (
      word.idexpression === currentWord.idexpression
        ? { ...word, eng: trimmedEng, pl: trimmedPl }
        : word
    ));
    syncUpdatedWords(updatedWords, currentWord.idexpression);
    setShowEditModal(false);
    setIsRevealed(false);
  };

  const handleMoveCurrentWord = async (targetDictId: number) => {
    if (!currentWord.idexpression) {
      return;
    }

    await moveWord(
      currentWord.idexpression,
      targetDictId,
      showSpinner,
      hideSpinner,
      showToast,
      () => {
        const movedWord = { ...currentWord, iddictionary: targetDictId };
        const updatedAllWords = allWords.map((word) => (
          word.idexpression === movedWord.idexpression ? movedWord : word
        ));

        if (iddictionary && String(targetDictId) !== iddictionary) {
          const remainingWords = displayWords.filter((word) => word.idexpression !== movedWord.idexpression);
          setAllWords(updatedAllWords.filter((word) => word.idexpression !== movedWord.idexpression));
          setDisplayWords(remainingWords);
          setCurrentIndex((prev) => Math.min(prev, Math.max(remainingWords.length - 1, 0)));
        } else {
          syncUpdatedWords(updatedAllWords, movedWord.idexpression);
        }

        setShowMoveMenu(false);
        setIsRevealed(false);
      }
    );
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    const word = displayWords[currentIndex];
    if (!word.idexpression) return;
    try {
      showSpinner();
      const resp = await fetchWithAuth(getApiUrl(`words/delete/${word.idexpression}`), { method: "DELETE" });
      if (!resp.ok) { showToast("Failed to delete expression.", "error"); return; }
      const updated = displayWords.filter((_, i) => i !== currentIndex);
      setAllWords(prev => prev.filter(w => w.idexpression !== word.idexpression));
      setDisplayWords(updated);
      setCurrentIndex(prev => Math.min(prev, updated.length - 1));
      setIsRevealed(false);
      document.dispatchEvent(new CustomEvent("wordcounts:refresh"));
    } catch { showToast("Error deleting expression.", "error"); }
    finally { hideSpinner(); }
  };

  const handleToggleShuffle = () => {
    const next: OrderMode =
      orderMode === "shuffle" ? "sequential" : orderMode === "sequential" ? "repeat" : "shuffle";
    setOrderMode(next);
    localStorage.setItem("flashcards_order_mode", next);

    if (next === "shuffle") {
      setDisplayWords(shuffleArray([...allWords]));
    } else {
      // sequential and repeat both use alphabetical order
      setDisplayWords([...allWords].sort((a, b) => a.eng.localeCompare(b.eng)));
    }
    setCurrentIndex(0);
    setIsRevealed(false);
    setIsAutoPlaying(false);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAutoPlaying) return;

      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (!isRevealed) setIsRevealed(true);
        else handleNext();
      } else if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handlePrevious();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isRevealed, handleNext, isAutoPlaying]);

  if (isLoading) return null;

  if (displayWords.length === 0) {
    return (
      <div className="flashcards-container">
        <PageHeader
          title="Flashcards"
          icon="bi-front"
        />
        <EmptyState
          icon="bi bi-card-text"
          title="No expressions available"
          description={
            iddictionary
              ? "This dictionary doesn't have any expressions yet. Add some expressions to start practicing!"
              : "You don't have any expressions yet. Create a dictionary and add expressions to get started!"
          }
        />
      </div>
    );
  }

  const currentWord = displayWords[currentIndex];
  const progress = ((currentIndex + 1) / displayWords.length) * 100;
  const isLastCard = currentIndex === displayWords.length - 1;

  const primaryWord =
    languageMode === "eng" ? currentWord.eng : currentWord.pl;
  const secondaryWord =
    languageMode === "eng" ? currentWord.pl : currentWord.eng;

  const targetLangName = langName || "English";
  const targetLangShort = (langCode || "en").toUpperCase();

  const primaryLabel = languageMode === "eng" ? targetLangName : "Polish";
  const secondaryLabel = languageMode === "eng" ? "Polish" : targetLangName;

  return (
    <div className="flashcards-container">
        <PageHeader
          title="Flashcards"
          icon="bi-front"
        />

      <div className="flashcards-header">
        <div className="flashcards-progress-info">
          <span className="flashcards-progress-text">
            {currentIndex + 1} / {displayWords.length}
          </span>
        </div>

        <div className="flashcards-language-toggle">
          <button
            className={`btn ${languageMode === "eng" ? "btn-primary" : "btn-light"}`}
            onClick={() => { setLanguageMode("eng"); setIsRevealed(false); }}
            disabled={isAutoPlaying}
          >
            {targetLangShort} → POL
          </button>
          <button
            className={`btn ${languageMode === "pol" ? "btn-primary" : "btn-light"}`}
            onClick={() => { setLanguageMode("pol"); setIsRevealed(false); }}
            disabled={isAutoPlaying}
          >
            POL → {targetLangShort}
          </button>
        </div>

        <button 
          className="btn btn-primary" 
          onClick={handleToggleShuffle} 
          title={
            orderMode === "shuffle" ? "Shuffle mode" 
            : orderMode === "sequential" ? "Sequential mode" 
            : "Repeat mode"
          }
          disabled={isAutoPlaying}
        >
          <i className={`bi ${
            orderMode === "shuffle" ? "bi-shuffle" 
            : orderMode === "sequential" ? "bi-list-ol" 
            : "bi-repeat"
          }`}></i>
        </button>
      </div>

      <div className="flashcards-progress-bar-container">
        <div className="flashcards-progress-bar" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="flashcard-area">
        <div
          key={currentIndex}
          className={`flashcard ${isRevealed ? "flashcard-revealed" : ""} ${isAutoPlaying ? "autoplay" : ""}`}
          onClick={handleCardClick}
          /* TOUCH */
          onTouchStart={(e) => startDrag(e.touches[0].clientX)}
          onTouchMove={(e) => moveDrag(e.touches[0].clientX)}
          onTouchEnd={endDrag}

          /* MOUSE */
          onMouseDown={(e) => startDrag(e.clientX)}
          onMouseMove={(e) => {
            if (dragStartX !== null) moveDrag(e.clientX);
          }}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}          
          role="button"
          tabIndex={0}
          aria-label={isRevealed ? "Card flashcard-revealed" : "Click to reveal translation"}
          
        >
          <div className="flashcard-card-content">
            <div className="flashcard-word-primary">
              <span className="flashcard-word-label">{primaryLabel}</span>
              <span className="flashcard-word-text">{primaryWord}</span>
              
            </div>
            
            {!isRevealed && !isAutoPlaying && (
              <div className="flashcard-tap-hint">
                <span>Tap to reveal</span>
                <i className="bi bi-hand-index"></i>
              </div>
            )}

            <div className={`flashcard-word-secondary ${isRevealed ? "flashcard-visible" : ""}`}>
              <div className="flashcard-divider"></div>
              <span className="flashcard-word-label">{secondaryLabel}</span>
              <span className="flashcard-translation-text">{secondaryWord}</span>
              
            </div>
          </div>
          {!isAutoPlaying && (
            <div className="flashcard-actions-btn-area" ref={actionsRef}>
              <div className="flashcard-actions-btn-group flashcard-actions-btn-group--left">
                <button
                  type="button"
                  className="flashcard-action-btn flashcard-action-btn--move"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMoveMenu((prev) => !prev);
                  }}
                  title="Move expression"
                  aria-label="Move expression"
                >
                  {moveToIcon}
                </button>
                <EditButton onClick={() => openEditModal()} title="Edit expression" />
                <DeleteButton onClick={handleDeleteClick} title="Delete expression" />

                {showMoveMenu && (
                  <div className="dropdown-menu show words-row-header-action-menu-button flashcard-move-menu">
                    <div className="dropdown-header">Move to Dictionary</div>
                    {dictionaries.length === 0 ? (
                      <div className="dropdown-item disabled">No dictionaries</div>
                    ) : (
                      dictionaries
                        .filter((dictionary) => dictionary.iddictionary !== currentWord.iddictionary)
                        .map((dictionary) => (
                          <button
                            key={dictionary.iddictionary}
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveCurrentWord(dictionary.iddictionary);
                            }}
                          >
                            {dictionary.name}
                          </button>
                        ))
                    )}
                  </div>
                )}
              </div>

              <div className="flashcard-actions-btn-group flashcard-actions-btn-group--right">
                <SpeakButton text={currentWord.eng} lang={langCode} size="large" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flashcard-speak-info">
        <span className="flashcard-speak-info__label">This application uses the Web Speech API.</span>
        <button
          className="flashcard-speak-info__btn"
          onClick={() => setShowSpeechInfo(prev => !prev)}
          title="More info"
          aria-expanded={showSpeechInfo}
        >
          <i className="bi bi-info-circle"></i>
        </button>
        {showSpeechInfo && (
          <div className="flashcard-speak-info__details">
            This app uses the Web Speech API (built into modern browsers) to convert text to speech directly on your device. Speech is generated using the voices installed on your operating system. If speech doesn't work, you'll need to install the English and Polish languages.
          </div>
        )}
      </div>

      <div className="flashcard-controls">
        <button 
          className="btn btn-light"
          onClick={handlePrevious}
          disabled={currentIndex === 0 || isAutoPlaying}
          title="Previous card (←)"
        >
          <i className="bi bi-chevron-left"></i>
          <span>Previous</span>
        </button>

        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={isLastCard || isAutoPlaying}
          title="Next card (→ or Space)"
        >
          <span>Next</span>
          <i className="bi bi-chevron-right"></i>
        </button>

        <button 
          className={`btn btn-success ${isAutoPlaying ? "active playing" : ""}`} 
          onClick={handleToggleAutoPlay} 
          title={isAutoPlaying ? "Stop autoplay" : "Start autoplay"}
        >
          <i className={`bi ${isAutoPlaying ? "bi-pause-circle" : "bi-play-circle"}`}></i>
          <span>{isAutoPlaying ? "Stop" : "Play"}</span>
        </button>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Expression"
        message={`Delete "${
          languageMode === "eng"
            ? displayWords[currentIndex]?.eng
            : displayWords[currentIndex]?.pl
        }"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <WordModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onSave={handleSaveEdit}
        canAddWord={true}
        remainingExpressions={0}
        maxExpressions={-1}
        langName={langName}
        mode="edit"
        initialEng={currentWord.eng}
        initialPl={currentWord.pl}
      />
    </div>
  );
};

export default Flashcards;